import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Calendar, MapPin, Tag } from "lucide-react";
import axios from "axios";
import Navbar from "./Navbar";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeDestination, setActiveDestination] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeDestination) params.set("destination", activeDestination);
    if (activeCategory) params.set("category", activeCategory);
    axios.get(`${API}/blog?${params.toString()}`)
      .then((r) => {
        setPosts(r.data.posts);
        setDestinations(r.data.destinations);
        setCategories(r.data.categories);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeDestination, activeCategory]);

  const filtered = searchQuery.trim()
    ? posts.filter((p) => p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
    : posts;

  return (
    <div data-testid="blog-page" className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-[#0D1B2A]">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D1B2A] via-[#0D1B2A]/95 to-[#174358]/80" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white/30 mb-4">Blog</p>
          <h1 data-testid="blog-heading" className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl md:text-6xl font-light text-white leading-tight mb-4">
            Destination <em className="text-white/60">Insights</em>
          </h1>
          <p className="text-base text-white/45 max-w-xl">Expert guides, hotel reviews, and culinary trails — curated for travel agents by our on-ground teams.</p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[60px] z-30 bg-white border-b border-[#0D1B2A]/06 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          {/* Search */}
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0D1B2A]/30" />
              <input
                data-testid="blog-search"
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-full bg-[#F8FAFB] border border-[#0D1B2A]/08 text-sm text-[#0D1B2A] placeholder:text-[#0D1B2A]/30 focus:outline-none focus:border-[#174358]/30 transition-colors"
              />
            </div>
          </div>

          {/* Destination pills */}
          <div className="mb-3">
            <p className="text-[10px] text-[#0D1B2A]/35 uppercase tracking-wider font-semibold mb-2">Destinations</p>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="dest-filter-all" onClick={() => setActiveDestination("")}
                className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-wide font-semibold transition-all ${!activeDestination ? "bg-[#174358] text-white" : "text-[#0D1B2A]/40 hover:bg-[#0D1B2A]/05"}`}>All</button>
              {destinations.map((d) => (
                <button key={d} data-testid={`dest-filter-${d.toLowerCase()}`} onClick={() => setActiveDestination(d)}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-wide font-semibold transition-all ${activeDestination === d ? "bg-[#174358] text-white" : "text-[#0D1B2A]/40 hover:bg-[#0D1B2A]/05"}`}>{d}</button>
              ))}
            </div>
          </div>

          {/* Category pills */}
          <div>
            <p className="text-[10px] text-[#0D1B2A]/35 uppercase tracking-wider font-semibold mb-2">Categories</p>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="cat-filter-all" onClick={() => setActiveCategory("")}
                className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-wide font-semibold transition-all ${!activeCategory ? "bg-[#0D1B2A] text-white" : "text-[#0D1B2A]/40 hover:bg-[#0D1B2A]/05"}`}>All</button>
              {categories.map((c) => (
                <button key={c} data-testid={`cat-filter-${c.toLowerCase()}`} onClick={() => setActiveCategory(c)}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-wide font-semibold transition-all ${activeCategory === c ? "bg-[#0D1B2A] text-white" : "text-[#0D1B2A]/40 hover:bg-[#0D1B2A]/05"}`}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 md:py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#174358]/20 border-t-[#174358] rounded-full animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div data-testid="blog-no-results" className="text-center py-20">
              <p className="text-[#0D1B2A]/40 text-sm mb-2">No articles found.</p>
              <button onClick={() => { setActiveDestination(""); setActiveCategory(""); setSearchQuery(""); }} className="text-xs text-[#174358] font-semibold hover:underline">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.slug} data-testid={`blog-card-${post.slug}`}
                  className="bg-white rounded-2xl overflow-hidden border border-[#0D1B2A]/04 hover:border-[#174358]/12 hover:shadow-lg transition-all group block">
                  <div className="h-52 overflow-hidden">
                    <img src={post.image_url} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] bg-[#174358]/08 text-[#174358]/70 px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1">
                        <MapPin size={9} /> {post.destination}
                      </span>
                      <span className="text-[10px] bg-[#0D1B2A]/05 text-[#0D1B2A]/50 px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1">
                        <Tag size={9} /> {post.category}
                      </span>
                    </div>
                    <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#0D1B2A] mb-2 group-hover:text-[#174358] transition-colors leading-snug">{post.title}</h3>
                    <p className="text-xs text-[#0D1B2A]/45 leading-relaxed line-clamp-2 mb-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-[#0D1B2A]/06">
                      <span className="text-[10px] text-[#0D1B2A]/30 flex items-center gap-1">
                        <Calendar size={10} /> {post.created_at ? new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                      </span>
                      <span className="text-xs font-semibold text-[#174358] group-hover:text-[#1e5a75] inline-flex items-center gap-1 transition-colors">
                        Read <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
